import { Injectable } from '@angular/core';
import { ClinicOfficeListItem } from '../common/models/internal/clinic-office-list-item.model';
import { BehaviorSubject, catchError, of, take, forkJoin, Subscription, finalize, takeUntil, Subject, first } from 'rxjs';
import { SortingHeadersEnum } from '../sections/home-page/home-table/enums/sorting-headers-enum';
import { RequestsService } from './requests.service';
import { ClinicItemJSON } from '../common/models/server-response/clinic-item-json.model';
import { ClinicTypesEnum } from '../common/enums/clinic-types-enum';
import { ENTOfficeItemJSON } from '../common/models/server-response/ent-office-item-json.model';
import { ShopStateEnum } from '../common/enums/shop-state-enum';
import { CountriesEnum } from 'src/common-panel/enums/countries-enum';
import { formatDate } from '@angular/common';
import { LookUpTablesTimestampsJSON } from '../common/models/server-response/look-up-tables-timestamps-json.model';
import { ServerValueInterpreterService } from 'src/common-panel/services/server-value-interpreter.service';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { ShopTypesEnum } from 'src/common-panel/enums/shop-types-enum';
import { BrandTypesEnum } from 'src/common-panel/enums/brand-types-enum';
import { COUNTRIES_IDS, CountryId } from 'src/common-panel/constants/country-configs.constants';

@Injectable({
  providedIn: 'root'
})
export class ClinicOfficeListService
{
  subscription: Subscription = new Subscription()
  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  data: ClinicOfficeListItem[] = []
  loadedClinicOfficeListSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  isOpenMapDisabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)

  filterByIdCityOrName: BehaviorSubject<string> = new BehaviorSubject<string>('')
  filterByShopType: BehaviorSubject<ShopTypesEnum> = new BehaviorSubject<ShopTypesEnum>(ShopTypesEnum.clinic)
  filterByShopState: BehaviorSubject<ShopStateEnum> = new BehaviorSubject<ShopStateEnum>(ShopStateEnum.published)
  filterByCountry: BehaviorSubject<CountryId | null> = new BehaviorSubject<CountryId | null>(null)
  clearedFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
  needsToRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  countriesToUpdateLookupTable: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([])
  timestampsZipCodeRelationTooltip: BehaviorSubject<string> = new BehaviorSubject<string>("")
  isGenerateLookupTableLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  tableSettings: {
    sortingHeader: SortingHeadersEnum;
    sortDirection: SortingDirectionsEnum;
    pageSize: number;
    pageIndex: number;
    countryFilterState: CountryId | undefined;
  } = {
      sortingHeader: SortingHeadersEnum.name,
      sortDirection: SortingDirectionsEnum.none,
      pageSize: 20,
      pageIndex: 0,
      countryFilterState: undefined
    }

  constructor(private requestsService: RequestsService, private serverValueInterpreterService: ServerValueInterpreterService) { }

  clearAll()
  {
    this.ngUnsubscribe.complete()
    this.subscription.unsubscribe()
    this.subscription=new Subscription()
  }

  loadClinicsData(country: CountryId): BehaviorSubject<boolean>
  {
    this.ngUnsubscribe.next();
    this.loadedClinicOfficeListSubject.next(false)
    let l_sub = this.requestsService.getClinicList(country).pipe(
      take(1),
      takeUntil(this.ngUnsubscribe),
      catchError(err => { return of([]) })
    )
      .subscribe(l_clinics =>
      {
        let l_data: ClinicOfficeListItem[] = []
        let l_sub1 = forkJoin({
          patientsCount: this.requestsService.getCountryClinicPatientCount(country).pipe(take(1)),
          hcpsCount: this.requestsService.getCountryHCPCount(country).pipe(take(1)),
        }).subscribe(l_counts =>
        {
          l_clinics.forEach((l_clinic, index) =>
          {
            let l_hcpCount = l_counts.hcpsCount[l_clinic.shopId]
            let l_patientCount = l_counts.patientsCount[l_clinic.shopId]
            let l_checkedClinic = this.checkClinicData(l_clinic, ShopTypesEnum.clinic, false)

            if (l_checkedClinic)
            {
              l_checkedClinic!.patientsAmount = l_patientCount ? l_patientCount : 0;
              l_checkedClinic!.hcpAmount = l_hcpCount ? l_hcpCount : 0;
              l_data.push(l_checkedClinic!)
            }

          })
          this.data = l_data
          this.loadedClinicOfficeListSubject.next(true)
        })
        this.subscription.add(l_sub1)

      })

    this.subscription.add(l_sub)
    return this.loadedClinicOfficeListSubject
  }

  loadOfficesData(country: CountryId): BehaviorSubject<boolean>
  {
    this.ngUnsubscribe.next();
    this.loadedClinicOfficeListSubject.next(false)
    let l_sub = this.requestsService.getOfficeList(country).pipe(
      take(1),
      takeUntil(this.ngUnsubscribe),
      catchError(err => { return of([]) })
    )
      .subscribe(l_offices =>
      {
        let l_data: ClinicOfficeListItem[] = []

        let l_sub1 = forkJoin({
          patientsCount: this.requestsService.getCountryOfficePatientCount(country).pipe(take(1)),
          entsCount: this.requestsService.getCountryENTCount(country).pipe(take(1)),
        }).subscribe(l_counts =>
        {
          l_offices.forEach((l_office, index) =>
          {
            let l_entsCount = l_counts.entsCount[l_office.id]
            let l_patientCount = l_counts.patientsCount[l_office.id]
            let l_checkedOffice = this.checkOfficeData(l_office, ShopTypesEnum.entOffice, false)

            if (l_checkedOffice)
            {
              l_checkedOffice!.patientsAmount = l_patientCount ? l_patientCount : 0;
              l_checkedOffice!.entAmount = l_entsCount ? l_entsCount : 0;
              l_data.push(l_checkedOffice!)
            }

          })
          this.data = l_data
          this.loadedClinicOfficeListSubject.next(true)
        })
        this.subscription.add(l_sub1)

      })

    this.subscription.add(l_sub)

    return this.loadedClinicOfficeListSubject
  }

  loadRequestedClinicsData(country: CountryId): BehaviorSubject<boolean>
  {
    this.ngUnsubscribe.next();
    this.loadedClinicOfficeListSubject.next(false)
    let l_sub = this.requestsService.getClinicRequestsList(country).pipe(
      take(1),
      takeUntil(this.ngUnsubscribe),
      catchError(err => { return of([]) })
    )
      .subscribe(l_clinics =>
      {
        let l_data: ClinicOfficeListItem[] = []
        let l_sub1 = forkJoin({
          patientsCount: this.requestsService.getCountryClinicPatientCount(country).pipe(take(1)),
          hcpsCount: this.requestsService.getCountryHCPCount(country).pipe(take(1)),
        }).subscribe(l_counts =>
        {
          l_clinics.forEach((l_clinic, index) =>
          {
            let l_historyLength = l_clinic.auditTrail.history.length
            let l_hcpCount = l_counts.hcpsCount[l_clinic.id]
            let l_patientCount = l_counts.patientsCount[l_clinic.id]
            let l_checkedClinic = this.checkClinicData(l_clinic.auditTrail.history[l_historyLength - 1].item, ShopTypesEnum.clinic, true)
            if (l_checkedClinic)
            {
              l_checkedClinic!.patientsAmount = l_patientCount ? l_patientCount : 0;
              l_checkedClinic!.hcpAmount = l_hcpCount ? l_hcpCount : 0;
              l_data.push(l_checkedClinic!)


            }
          })
          this.data = l_data
          this.loadedClinicOfficeListSubject.next(true)
        })


        this.subscription.add(l_sub1)

      })

    this.subscription.add(l_sub)

    return this.loadedClinicOfficeListSubject
  }

  loadRequestedOfficesData(country: CountryId): BehaviorSubject<boolean>
  {
    this.ngUnsubscribe.next();
    this.loadedClinicOfficeListSubject.next(false)
    let l_sub = this.requestsService.getOfficeRequestsList(country).pipe(
      take(1),
      takeUntil(this.ngUnsubscribe),
      catchError(err => { return of([]) })
    )
      .subscribe(l_offices =>
      {
        let l_data: ClinicOfficeListItem[] = []

        let l_sub1 = forkJoin({
          patientsCount: this.requestsService.getCountryOfficePatientCount(country).pipe(take(1)),
          entsCount: this.requestsService.getCountryENTCount(country).pipe(take(1)),
        }).subscribe(l_counts =>
        {
          l_offices.forEach((l_office, index) =>
          {
            let l_historyLength = l_office.auditTrail.history.length
            let l_entsCount = l_counts.entsCount[l_office.id]
            let l_patientCount = l_counts.patientsCount[l_office.id]
            let l_checkedOffice = this.checkOfficeData(l_office.auditTrail.history[l_historyLength - 1].item, ShopTypesEnum.entOffice, true)

            if (l_checkedOffice)
            {
              l_checkedOffice!.patientsAmount = l_patientCount ? l_patientCount : 0;
              l_checkedOffice!.entAmount = l_entsCount ? l_entsCount : 0;
              l_data.push(l_checkedOffice!)
            }

          })
          this.data = l_data
          this.loadedClinicOfficeListSubject.next(true)
        })
        this.subscription.add(l_sub1)

      })

    this.subscription.add(l_sub)

    return this.loadedClinicOfficeListSubject
  }

  checkClinicData(clinicListItem: ClinicItemJSON, type1: ShopTypesEnum, waitsForPublishing: boolean): ClinicOfficeListItem | null
  {

    if (!clinicListItem.shopId)
    {
      console.error(`Error: Clinic ${ JSON.stringify(clinicListItem) } hasn't ID.`)

      return null;
    }

    let l_error: string[] = []

    if (!clinicListItem.country || !Object.values(CountriesEnum).includes(clinicListItem.country as CountriesEnum))
    {
      l_error.push(` - hasn't country ot country is undefined (${ clinicListItem.country }).`)
    }

    if (!clinicListItem.email)
    {
      l_error.push(` - hasn't E-mail.`)
    }

    if (!clinicListItem.shopName)
    {
      l_error.push(` - hasn't Shop Name.`)
    }

    if (!clinicListItem.brandId)
    {
      l_error.push(` - hasn't brandID.`)
    }

    if (!clinicListItem.city)
    {
      l_error.push(` - hasn't city.`)
    }

    if (!clinicListItem.zipCode)
    {
      l_error.push(` - hasn't zipCode.`)
    }

    if (!clinicListItem.phone)
    {
      l_error.push(` - hasn't phone.`)
    }

    if (!clinicListItem.openingHours)
    {
      l_error.push(` - hasn't openingHours.`)
    }

    if (!clinicListItem.appleMaps.geolocation)
    {
      l_error.push(` - hasn't geolocation.`)
    }

    if (!clinicListItem.shopType)
    {
      l_error.push(` - hasn't shopType.`)
    }

    if (!clinicListItem.addressLine1)
    {
      l_error.push(` - hasn't addressLine1.`)
    }


    if (l_error.length > 0)
    {
      console.error(` Error in clinic ${ JSON.stringify(clinicListItem) }:\n` + l_error.join(';\n'))
    }


    let l_clinicOfficeListItem: ClinicOfficeListItem = {
      clinicOfficeId: clinicListItem.shopId,
      entOfficeId: clinicListItem.shopId,
      patientsAmount: 0,
      hcpAmount: 0,
      entAmount: 0,
      shown: clinicListItem.presentedForSelection,
      email: clinicListItem.email,
      name: clinicListItem.shopName,
      brand: clinicListItem.brandId as BrandTypesEnum,
      city: clinicListItem.city,
      crmType: clinicListItem.crmType?clinicListItem.crmType:null,
      countryId: this.serverValueInterpreterService.getIdByFullCountryName(clinicListItem.country as CountriesEnum),
      waitsForPublishing: waitsForPublishing,
      state: clinicListItem.state?clinicListItem.state:'',
      zipCode: clinicListItem.zipCode,
      phone: clinicListItem.phone,
      website: '',
      openingHours: clinicListItem.openingHours.length > 0 ? clinicListItem.openingHours : [[], [], [], [], [], [], []],
      geolocation: {
        longitude: clinicListItem.appleMaps.geolocation.longitude.toString(),
        latitude: clinicListItem.appleMaps.geolocation.latitude.toString()
      },
      shopType: clinicListItem.shopType as ClinicTypesEnum,
      addressLine1: clinicListItem.addressLine1,
      addressLine2: clinicListItem.addressLine2,
      type1: type1,
      doctors: []

    }
    return l_clinicOfficeListItem;
  }

  checkOfficeData(officeListItem: ENTOfficeItemJSON, type1: ShopTypesEnum, waitsForPublishing: boolean): ClinicOfficeListItem | null
  {

    if (!officeListItem.id)
    {
      console.error(`Error:  Office ${ JSON.stringify(officeListItem) } hasn't ID.`)

      return null;
    }
    let l_error: string[] = []

    if (!officeListItem.email)
    {
      l_error.push(` - hasn't E-mail.`)

    }

    if (!officeListItem.entOfficeName)
    {
      l_error.push(` - hasn't Officce Name.`)
    }

    if (!officeListItem.city)
    {
      l_error.push(` - hasn't city.`)
    }

    if (!officeListItem.country)
    {
      l_error.push(` - hasn't country.`)
    }

    if (!officeListItem.zipCode)
    {
      l_error.push(` - hasn't zipCode.`)
    }

    if (!officeListItem.phone)
    {
      l_error.push(` - hasn't phone.`)
    }

    if (!officeListItem.doctors)
    {
      l_error.push(` - hasn't openingHours.`)
    }

    if (!officeListItem.appleMaps.geolocation)
    {
      l_error.push(` - hasn't geolocation.`)
    }


    if (!officeListItem.addressLine1)
    {
      l_error.push(` - hasn't addressLine1.`)
    }

    if (l_error.length > 0)
    {
      console.error(` Error in office ${ JSON.stringify(officeListItem) }:\n` + l_error.join(';\n'))
    }

    let l_clinicOfficeListItem: ClinicOfficeListItem = {
      clinicOfficeId: officeListItem.id,
      entOfficeId: officeListItem.id,
      patientsAmount: 0,
      hcpAmount: 0,
      entAmount: 0,
       shown: !officeListItem.hidden,
      email: (officeListItem.email && officeListItem.email.length > 0) ? officeListItem.email[0] : '',
      name: officeListItem.entOfficeName,
      brand: undefined,
      city: officeListItem.city,
      countryId: officeListItem.countryId,
      waitsForPublishing: waitsForPublishing,
      state: officeListItem.state,
      zipCode: officeListItem.zipCode,
      website: officeListItem.webpage,
      phone: (officeListItem.phone && officeListItem.phone.length > 0) ? officeListItem.phone[0] : '',
      openingHours: officeListItem.openingHours.length > 0 ? officeListItem.openingHours : [[], [], [], [], [], [], []],
      geolocation: {
        longitude: officeListItem.appleMaps.geolocation.longitude,
        latitude: officeListItem.appleMaps.geolocation.latitude
      },
      shopType: ClinicTypesEnum.physical,
      addressLine1: officeListItem.addressLine1,
      addressLine2: officeListItem.addressLine2,
      type1: type1,
      doctors: officeListItem.doctors,
      crmType: null
    }
    return l_clinicOfficeListItem;
  }

  exportCSV(type: ShopTypesEnum, country: CountryId): BehaviorSubject<boolean>
  {
    let l_loaded = new BehaviorSubject<boolean>(false)
    if (type == ShopTypesEnum.clinic)
    {
      this.requestsService.getShopsCSV(country).pipe(
        take(1),
        finalize(() =>
        {
          l_loaded.next(true)
        })).subscribe((data: Blob) =>
        {
          var file = new Blob([data], { type: 'text/csv;charset=utf-8' })
          var fileURL = URL.createObjectURL(file);
          let l_date = formatDate(Date.now(), 'yyyy-MM-dd_HH-mm-ss', 'en-US');
          var a = document.createElement('a');
          a.href = fileURL;
          a.target = '_blank';
          a.download = `${ country }_${ type }_${ l_date }`;
          document.body.appendChild(a);
          a.click();
        },
          (error) =>
          {
            console.error('getCSV error: ', error);
          }
        );
    } else if (type == ShopTypesEnum.entOffice)
    {
      this.requestsService.getOfficesCSV(country).pipe(
        take(1),
        finalize(() =>
        {
          l_loaded.next(true)
        })).subscribe((data: Blob) =>
        {
          var file = new Blob([data], { type: 'text/csv;charset=utf-8' })
          var fileURL = URL.createObjectURL(file);
          let l_date = formatDate(Date.now(), 'yyyy-MM-dd_HH-mm-ss', 'en-US');
          var a = document.createElement('a');
          a.href = fileURL;
          a.target = '_blank';
          a.download = `${ country }_${ type }_${ l_date }`;
          document.body.appendChild(a);
          a.click();
        },
          (error) =>
          {
            console.error('getCSV error: ', error);
          }
        );
    }
    return l_loaded
  }

  generateZipLookupTable(countryId: string): BehaviorSubject<boolean>
  {
    let l_loaded = new BehaviorSubject<boolean>(false)

    this.requestsService.generateZipLookupTable(countryId).pipe(
      take(1),
      finalize(() =>
      {
        l_loaded.next(true)
      })).subscribe(() =>
      {
        //todo
      }
      );

    return l_loaded
  }

  getTimestampsZipCodeRelation()
  {
    this.isGenerateLookupTableLoadedSubject.next(false)
    this.requestsService.getTimestampsZipCodeRelation().pipe(
      take(1)).subscribe(l_response =>
      {
        let l_resText = ""
        Object.values(COUNTRIES_IDS).forEach(countryId =>
        {
          if (Object.keys(l_response).includes(countryId))
          {
            let l_resDate
            if (l_response[countryId] != null)
            {
              let l_date = new Date(l_response[countryId]!)
              if (!isNaN(l_date.getTime())) l_resDate = formatDate(l_date, 'yyyy-MM-dd', "en-US")

            }


            if (l_response[countryId])
              l_resText += this.serverValueInterpreterService.getFullCountryNameById(countryId) + ": " + l_resDate + ";\n"
          }
        });
        this.getCountriesToUpdateLookUpTable(l_response).pipe(first(val=>val==true)).subscribe(res=>{
          this.isGenerateLookupTableLoadedSubject.next(res)
        })
        this.timestampsZipCodeRelationTooltip.next(l_resText)
      }
      );
     
  }


  getCountriesToUpdateLookUpTable(timestampsZipCodeRelation: LookUpTablesTimestampsJSON)
  {
    let l_loaded = new BehaviorSubject<boolean>(false)

    this.requestsService.getTimestampsFromAdminDb().pipe(
      take(1),
      finalize(() =>
      {
        l_loaded.next(true)
      })).subscribe(l_response =>
      {
        var l_countriesToUpdate: string[] = []
        l_response.forEach(l_country =>
        {
          if (Object.values(COUNTRIES_IDS).includes(l_country.countryId))
          {
            if (Object.keys(timestampsZipCodeRelation).includes(l_country.countryId))
            {
              let l_date = new Date(timestampsZipCodeRelation[l_country.countryId]!)
              
              if (isNaN(l_date.getTime()) || (l_date.getTime()/1000 < l_country._ts))
              {
                l_countriesToUpdate.push(l_country.countryId);
              }
            } else
            {
              l_countriesToUpdate.push(l_country.countryId);
            }
          }
        });
        this.countriesToUpdateLookupTable.next(l_countriesToUpdate);

      }
      );

    return l_loaded
  }
}
